import { Inject, inject, Injectable } from '@angular/core';
import { CredentialsTokensType, KeycloakException } from '@cargos/sprintpay-models';
import { KeycloakService } from '@cargos/sprintpay-services';
import { BehaviorSubject, map, Observable, of, switchMap, take, tap } from 'rxjs';
import Swal from 'sweetalert2';
import { KEYCLOAK_INITIAL_HASH } from './keycloak-redirect.token';
@Injectable({
    providedIn: 'root',
})
export class KeycloakHandlerService {
    private readonly authenticated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly keycloakService = inject(KeycloakService);
    private redirectFromKeycloak: boolean;

    constructor(@Inject(KEYCLOAK_INITIAL_HASH) initialHash: string) {
        this.redirectFromKeycloak = this.detectKeycloakRedirect(initialHash);
        this.getToken();
    }

    private detectKeycloakRedirect(hash: string): boolean {
        if (!hash || hash.length <= 1) {
            return false;
        }

        const params = new URLSearchParams(hash.substring(1));
        return params.has('state') && params.has('session_state') && params.has('code');
    }

    consumeRedirectFromKeycloak(): boolean {
        const wasRedirect = this.redirectFromKeycloak;
        this.redirectFromKeycloak = false;
        return wasRedirect;
    }

    logoutKeycloak(): Observable<string> {
        return this.keycloakService.logoutKeycloak().pipe(
            tap(() => {
                this.setAuthenticatedOnKeycloak(false);
            })
        );
    }

    setAuthenticatedOnKeycloak(isAuthenticated: boolean): void {
        this.authenticated.next(isAuthenticated);
    }

    isAuthenticatedOnKeycloak(): Observable<boolean> {
        return this.authenticated.asObservable();
    }

    getToken(): string | undefined {
        const token = this.keycloakService.getToken();
        if (token) {
            this.setAuthenticatedOnKeycloak(true);
        }
        return token;
    }

    resetPassword(): Observable<string> {
        return this.keycloakService.resetPassword();
    }

    sendTokenToServer(): Observable<CredentialsTokensType> {
        return this.keycloakService.sendTokenToServer();
    }

    validateSession(): Observable<boolean> {
        return this.keycloakService.validateSession().pipe(
            map((isAuthenticated) => {
                this.setAuthenticatedOnKeycloak(isAuthenticated);
                return isAuthenticated;
            })
        );
    }

    validateKeycloakSessionAndLogin(): Observable<CredentialsTokensType | null> {
        return this.keycloakService.validateSession().pipe(
            switchMap((isAuthenticated) => {
                if (!isAuthenticated) {
                    return of(null);
                }

                return this.sendTokenToServer().pipe(
                    map((credentials) => {
                        this.setAuthenticatedOnKeycloak(true);
                        return credentials;
                    })
                );
            })
        );
    }

    loginKeycloakSession(email: string): Observable<string> {
        return this.keycloakService.loginKeycloak({ loginHint: email, prompt: 'login' });
    }

    refreshToken(): Observable<boolean> {
        return this.keycloakService.refreshToken();
    }

    forgotPassword(email: string): void {
        const resetUrl = this.keycloakService.getUrlPassword(email);
        window.location.href = resetUrl;
    }

    logoutIfAuthenticatedOnKeycloak(): Observable<string> {
        return this.keycloakService.validateSession().pipe(
            take(1),
            switchMap((authenticated) => {
                if (authenticated) {
                    return this.logoutKeycloak();
                }
                return of('');
            })
        );
    }

    showKeycloakError(error: KeycloakException): void {
        Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.description,
        });
    }
}
